import constant from 'constant';
import SockJS from 'sockjs-client';
import { Client, Message, over } from 'stompjs';

// One SockJS/STOMP connection shared by every feature that subscribes to a
// topic (calendar check-in refresh, caller-id rings, ...). Connects lazily on
// the first subscription, tears down when the last one unsubscribes, and
// reconnects with a delay if the socket drops while subscribers remain.

const RECONNECT_DELAY_MS = 5000;

type TopicCallback = (msg: Message) => void;

interface Subscription {
  topic: string;
  cb: TopicCallback;
  stompSub?: { unsubscribe: () => void };
}

const subs = new Map<symbol, Subscription>();
let client: Client | null = null;
let socketRef: { close: () => void } | null = null;
let connected = false;
// invalidates in-flight connects/reconnects when the last subscriber leaves
let generation = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | undefined;

const debug = process.env.NODE_ENV === 'development'
  ? (msg: string) => console.log('[socket]', msg)
  : () => { };

const attach = (sub: Subscription) => {
  if (!client || !connected) return;
  debug(`subscribing ${sub.topic}`);
  sub.stompSub = client.subscribe(sub.topic, sub.cb);
};

const teardown = () => {
  generation++;
  clearTimeout(reconnectTimer);
  connected = false;
  if (client?.connected) client.disconnect(() => { });
  // the raw SockJS may still be mid-handshake — close it unconditionally
  try { socketRef?.close(); } catch { /* already closed */ }
  client = null;
  socketRef = null;
  subs.forEach(s => { s.stompSub = undefined; });
};

const connect = () => {
  const gen = generation;
  const socket = new SockJS(constant.SOCKET_URL);
  socketRef = socket;
  const c = over(socket);
  c.debug = debug;
  const scheduleReconnect = () => {
    if (gen !== generation || subs.size === 0) return;
    connected = false;
    subs.forEach(s => { s.stompSub = undefined; });
    clearTimeout(reconnectTimer);
    reconnectTimer = setTimeout(() => {
      if (gen === generation && subs.size > 0) connect();
    }, RECONNECT_DELAY_MS);
  };
  c.connect({}, () => {
    if (gen !== generation || subs.size === 0) return teardown();
    connected = true;
    subs.forEach(attach);
    socket.onclose = scheduleReconnect;
  }, scheduleReconnect);
  client = c;
};

// Subscribe to a topic on the shared connection; returns unsubscribe.
export const subscribeSharedTopic = (topic: string, cb: TopicCallback): (() => void) => {
  const key = Symbol(topic);
  const sub: Subscription = { topic, cb };
  subs.set(key, sub);
  if (!client) connect();
  else attach(sub); // no-op until connected; the connect callback attaches all
  return () => {
    try { sub.stompSub?.unsubscribe(); } catch { /* connection already gone */ }
    subs.delete(key);
    if (subs.size === 0) teardown();
  };
};
